import type { PlaneContourData } from "../atoms/segmentationAtoms";

export interface WorldPolyline {
  points: [number, number, number][];
  plane: "axial" | "coronal" | "sagittal";
  sliceIndex: number;
}

/**
 * Project 2D contour polylines into 3D world space using the DICOM affine.
 *
 * For each slice z, each [y, x] point maps to:
 *   world = origin + x * spacing[2] * rowCosines
 *                   + y * spacing[1] * columnCosines
 *                   + z * spacing[0] * sliceDirection
 *
 * Contour points are [y, x]. Spacing is [z, y, x].
 */
export function planeContourDataToWorldPolylines(
  data: PlaneContourData,
  plane: "axial" | "coronal" | "sagittal",
): WorldPolyline[] {
  const { contours, spacing, origin, rowCosines, columnCosines, sliceDirection } = data;
  const [sliceSp, rowSp, colSp] = spacing;
  const polylines: WorldPolyline[] = [];

  for (const [sliceKey, sliceContours] of Object.entries(contours)) {
    const z = Number(sliceKey);
    for (let cIdx = 0; cIdx < sliceContours.length; cIdx++) {
      const contour = sliceContours[cIdx];
      if (contour.length < 3) continue;
      const points: [number, number, number][] = Array.from({ length: contour.length });
      for (let i = 0; i < contour.length; i++) {
        const [y, x] = contour[i];
        points[i] = [
          origin[0] +
            x * colSp * rowCosines[0] +
            y * rowSp * columnCosines[0] +
            z * sliceSp * sliceDirection[0],
          origin[1] +
            x * colSp * rowCosines[1] +
            y * rowSp * columnCosines[1] +
            z * sliceSp * sliceDirection[1],
          origin[2] +
            x * colSp * rowCosines[2] +
            y * rowSp * columnCosines[2] +
            z * sliceSp * sliceDirection[2],
        ];
      }
      polylines.push({ points, plane, sliceIndex: z });
    }
  }

  return polylines;
}
